import type { Template, LabelElement, AssetReference, TextElement, ImageElement } from './types';
import { fetchAssetContent, type AssetApi } from './assetService';

/** Normalize a legacy mongo-id value (may be string or string[]) into a flat array of individual IDs */
export function normalizeLegacyIds(mongoId: string | string[]): string[] {
  if (Array.isArray(mongoId)) return mongoId;
  return [mongoId];
}

export type LegacyAssetResolverOptions = {
  apiBaseUrl: string;
  organizationId: string;
  getToken: () => string | null;
};

export type LegacyAssetResolver = {
  resolveImageLink: (mongoId: string) => Promise<string | null>;
  resolveFontAsset: (mongoId: string) => Promise<string | null>;
  preloadTemplateAssets: (template: Template) => Promise<{
    images: Record<string, string>;
    fonts: Record<string, string>;
  }>;
};

/**
 * Creates a legacy asset resolver that fetches assets from the BE endpoint.
 * 
 * Flow for images:
 * 1. Call GET /filesystem/link/legacy?organizationId={orgId}&legacyFileId={mongoId}
 * 2. The endpoint returns the Azure blob URL directly
 * 3. Use the URL directly in <img src> (CORS is configured on Azure)
 * 
 * Flow for fonts:
 * 1. Call GET /filesystem/legacy?organizationId={orgId}&legacyFileId={mongoId}
 * 2. The endpoint proxies the asset from Azure and returns the binary data
 * 3. Convert to base64 data URL for @font-face injection
 */
export function createLegacyAssetResolver(
  options: LegacyAssetResolverOptions
): LegacyAssetResolver {
  const { apiBaseUrl, organizationId, getToken } = options;

  /**
   * Resolve a legacy image by MongoDB ID to an Azure blob URL
   */
  async function resolveImageLink(mongoId: string): Promise<string | null> {
    try {
      const token = getToken();
      if (!token) {
        console.warn('[legacyAssets] No auth token available');
        return null;
      }

      // Get direct Azure blob URL from backend
      const linkUrl = `${apiBaseUrl}/filesystem/link/legacy?organizationId=${encodeURIComponent(organizationId)}&legacyFileId=${encodeURIComponent(mongoId)}`;
      
      const response = await fetch(linkUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn(`[legacyAssets] Failed to get image link ${mongoId}: ${response.status}`);
        return null;
      }

      // Response is the Azure blob URL as plain text
      const blobUrl = await response.text();
      return blobUrl.replace(/^"|"$/g, ''); // Strip quotes if present
    } catch (error) {
      console.error(`[legacyAssets] Error resolving image link ${mongoId}:`, error);
      return null;
    }
  }

  /**
   * Resolve a legacy font by MongoDB ID to a base64 data URL
   * (Fonts need base64 for @font-face injection)
   */
  async function resolveFontAsset(mongoId: string): Promise<string | null> {
    try {
      const token = getToken();
      if (!token) {
        console.warn('[legacyAssets] No auth token available');
        return null;
      }

      // Fetch font binary from backend (proxied from Azure)
      const assetUrl = `${apiBaseUrl}/filesystem/legacy?organizationId=${encodeURIComponent(organizationId)}&legacyFileId=${encodeURIComponent(mongoId)}`;
      
      const response = await fetch(assetUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn(`[legacyAssets] Failed to fetch font ${mongoId}: ${response.status}`);
        return null;
      }

      const blob = await response.blob();
      return await blobToBase64(blob);
    } catch (error) {
      console.error(`[legacyAssets] Error resolving font ${mongoId}:`, error);
      return null;
    }
  }

  /**
   * Resolve a legacy image by MongoDB ID to a base64 data URL
   * (Uses backend proxy to avoid CORS issues with Azure blob storage)
   */
  async function resolveImageAsset(mongoId: string): Promise<string | null> {
    try {
      const token = getToken();
      if (!token) {
        console.warn('[legacyAssets] No auth token available');
        return null;
      }

      // Fetch image binary from backend (proxied from Azure)
      const assetUrl = `${apiBaseUrl}/filesystem/legacy?organizationId=${encodeURIComponent(organizationId)}&legacyFileId=${encodeURIComponent(mongoId)}`;
      
      const response = await fetch(assetUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn(`[legacyAssets] Failed to fetch image ${mongoId}: ${response.status}`);
        return null;
      }

      const blob = await response.blob();
      return await blobToBase64(blob);
    } catch (error) {
      console.error(`[legacyAssets] Error resolving image ${mongoId}:`, error);
      return null;
    }
  }

  /**
   * Preload all legacy assets (images and fonts) from a template
   * - Images: resolved to base64 data URLs (via backend proxy to avoid CORS)
   * - Fonts: resolved to base64 data URLs (for @font-face injection)
   */
  async function preloadTemplateAssets(template: Template): Promise<{
    images: Record<string, string>;
    fonts: Record<string, string>;
  }> {
    const images: Record<string, string> = {};
    const fonts: Record<string, string> = {};

    // Collect all unique legacy asset IDs
    const imageIds = new Set<string>();
    const fontIds = new Set<string>();

    for (const element of template.elements) {
      if (element.type === 'image' && element['image-asset']?.['mongo-id']) {
        for (const id of normalizeLegacyIds(element['image-asset']['mongo-id'])) {
          imageIds.add(id);
        }
      }
      if (element.type === 'text' && element['font-asset']?.['mongo-id']) {
        for (const id of normalizeLegacyIds(element['font-asset']['mongo-id'])) {
          fontIds.add(id);
        }
      }
    }

    if (imageIds.size > 0 || fontIds.size > 0) {
      console.log(`[legacyAssets] Preloading ${imageIds.size} images and ${fontIds.size} fonts for template "${template.name || template.id}"`);
    }

    // Resolve images to base64 data URLs (via backend proxy to avoid CORS issues)
    const imagePromises = Array.from(imageIds).map(async (id) => {
      const dataUrl = await resolveImageAsset(id);
      if (dataUrl) {
        images[id] = dataUrl;
      } else {
        console.warn(`[legacyAssets] Failed to resolve image ${id} - no data returned`);
      }
    });

    // Resolve fonts to base64 data URLs (needed for @font-face injection)
    const fontPromises = Array.from(fontIds).map(async (id) => {
      const dataUrl = await resolveFontAsset(id);
      if (dataUrl) {
        fonts[id] = dataUrl;
      }
    });

    await Promise.all([...imagePromises, ...fontPromises]);

    if (imageIds.size > 0) {
      console.log(`[legacyAssets] Resolved ${Object.keys(images).length}/${imageIds.size} images`);
    }

    return { images, fonts };
  }

  return {
    resolveImageLink,
    resolveFontAsset,
    preloadTemplateAssets,
  };
}

/**
 * Inject a @font-face rule for a legacy font
 * Returns the font family name to use in CSS (quoted for safe use)
 */
export function injectLegacyFontFace(mongoId: string, base64DataUrl: string): string {
  const fontFamily = `legacy-font-${mongoId}`;
  const styleId = `legacy-font-style-${mongoId}`;

  // Check if already injected
  if (document.getElementById(styleId)) {
    return `'${fontFamily}'`;
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @font-face {
      font-family: '${fontFamily}';
      src: url('${base64DataUrl}') format('truetype');
      font-weight: normal;
      font-style: normal;
    }
  `;
  document.head.appendChild(style);

  // Return quoted font family name for CSS
  return `'${fontFamily}'`;
}

/**
 * Inject a @font-face rule for a new-style asset font
 * Returns the font family name to use in CSS
 * @param asset - The asset reference
 * @param fontSource - Either a base64 data URL or a regular URL to the font file
 */
export function injectNewAssetFontFace(asset: AssetReference, fontSource: string): string {
  // Use a sanitized version of the display name as the font family
  const sanitizedName = asset.displayName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '-');
  const fontFamily = `asset-font-${asset.fileId}-${sanitizedName}`;
  const styleId = `asset-font-style-${asset.fileId}`;

  // Check if already injected
  if (document.getElementById(styleId)) {
    return `'${fontFamily}'`;
  }

  // Determine the font format based on MIME type (decode URL-encoded mimeType)
  const decodedMimeType = decodeURIComponent(asset.mimeType).toLowerCase();
  let format = 'truetype';
  if (decodedMimeType.includes('woff2')) {
    format = 'woff2';
  } else if (decodedMimeType.includes('woff')) {
    format = 'woff';
  } else if (decodedMimeType.includes('opentype') || decodedMimeType.includes('otf')) {
    format = 'opentype';
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @font-face {
      font-family: '${fontFamily}';
      src: url('${fontSource}') format('${format}');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;
  document.head.appendChild(style);

  // Return quoted font family name for CSS
  return `'${fontFamily}'`;
}

/**
 * Get the effective font family for a text element
 * Priority:
 * 1. New asset reference (fontAsset) - uses injected @font-face
 * 2. Legacy asset reference (font-asset['mongo-id']) - uses injected @font-face
 * 3. Default font family
 */
export function getEffectiveFontFamily(
  element: { fontFamily: string; 'font-asset'?: { 'mongo-id': string | string[] }; fontAsset?: AssetReference },
  legacyFonts: Record<string, string>,
  defaultFont: string,
  newAssetFonts?: Record<string, string> // Map of blobName -> base64 data URL
): string {
  // Priority 1: New asset reference
  if (element.fontAsset?.blobName && newAssetFonts) {
    const base64 = newAssetFonts[element.fontAsset.blobName];
    if (base64) {
      return injectNewAssetFontFace(element.fontAsset, base64);
    }
  }
  
  // Priority 2: Legacy asset reference
  const fontAsset = element['font-asset'];
  if (fontAsset?.['mongo-id']) {
    const ids = normalizeLegacyIds(fontAsset['mongo-id']);
    for (const mongoId of ids) {
      const base64 = legacyFonts[mongoId];
      if (base64) {
        // Inject the font and return the family name
        return injectLegacyFontFace(mongoId, base64);
      }
    }
  }
  return defaultFont;
}

/**
 * Worker-safe version of getEffectiveFontFamily.
 * Returns the font family name string WITHOUT injecting @font-face CSS
 * into the DOM. Used inside Web Workers where document is not available.
 * The PDF generator resolves actual font bytes separately via resolveFontData.
 */
export function getEffectiveFontFamilyWorkerSafe(
  element: { fontFamily: string; 'font-asset'?: { 'mongo-id': string | string[] }; fontAsset?: AssetReference },
  legacyFonts: Record<string, string>,
  defaultFont: string,
  newAssetFonts?: Record<string, string>
): string {
  // Priority 1: New asset reference - compute family name without DOM injection
  if (element.fontAsset?.blobName && newAssetFonts) {
    const base64 = newAssetFonts[element.fontAsset.blobName];
    if (base64) {
      const sanitizedName = element.fontAsset.displayName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '-');
      return `'asset-font-${element.fontAsset.fileId}-${sanitizedName}'`;
    }
  }

  // Priority 2: Legacy asset reference - compute family name without DOM injection
  const fontAsset = element['font-asset'];
  if (fontAsset?.['mongo-id']) {
    for (const mongoId of normalizeLegacyIds(fontAsset['mongo-id'])) {
      if (legacyFonts[mongoId]) {
        return `'legacy-font-${mongoId}'`;
      }
    }
  }

  return defaultFont;
}

/**
 * Get the effective image URL for an image element
 * Priority:
 * 1. New asset reference (imageAsset) - resolved URL
 * 2. Legacy asset reference (image-asset['mongo-id']) - from cache
 * 3. Direct URL from element
 * Always returns a string (empty string if no URL available)
 */
export function getEffectiveImageUrl(
  element: { url?: string; 'image-asset'?: { 'mongo-id': string | string[] }; imageAsset?: AssetReference },
  legacyImages: Record<string, string>,
  newAssetImages?: Record<string, string> // Map of blobName -> resolved URL
): string {
  // Priority 1: New asset reference
  if (element.imageAsset?.blobName && newAssetImages) {
    const imageUrl = newAssetImages[element.imageAsset.blobName];
    if (imageUrl) {
      return imageUrl;
    }
  }
  
  // Priority 2: Legacy asset reference
  const imageAsset = element['image-asset'];
  if (imageAsset?.['mongo-id']) {
    const ids = normalizeLegacyIds(imageAsset['mongo-id']);
    for (const mongoId of ids) {
      const imageUrl = legacyImages[mongoId];
      if (imageUrl) {
        return imageUrl;
      }
    }
  }
  // Return empty string if url is undefined to avoid src={undefined}
  return element.url || '';
}

/**
 * Preload all new-style assets (fonts and images) from a template
 * Returns maps of blobName -> resolved URL/base64 data
 * 
 * @param template - The template containing asset references
 * @param api - The API client for resolving assets
 * @param organizationId - The organization ID
 * @param getToken - Optional function to get auth token (needed for font proxy)
 */
export async function preloadNewAssets(
  template: Template,
  api: AssetApi,
  organizationId: number,
  getToken?: () => string | null
): Promise<{
  images: Record<string, string>;
  fonts: Record<string, string>;
}> {
  const images: Record<string, string> = {};
  const fonts: Record<string, string> = {};

  // Collect all unique new-style asset references
  const imageAssets: AssetReference[] = [];
  const fontAssets: AssetReference[] = [];

  for (const element of template.elements) {
    if (element.type === 'image') {
      const imgEl = element as ImageElement;
      if (imgEl.imageAsset?.blobName) {
        imageAssets.push(imgEl.imageAsset);
      }
      if (imgEl.conditions) {
        for (const condition of imgEl.conditions) {
          if (condition.imageAsset?.blobName) {
            imageAssets.push(condition.imageAsset);
          }
        }
      }
    }
    if (element.type === 'text') {
      const textEl = element as TextElement;
      if (textEl.fontAsset?.blobName) {
        fontAssets.push(textEl.fontAsset);
      }
    }
  }

  if (imageAssets.length > 0 || fontAssets.length > 0) {
    console.log(`[legacyAssets] Preloading ${imageAssets.length} new images and ${fontAssets.length} new fonts for template "${template.name || template.id}"`);
  }

  const tokenGetter = getToken || (() => localStorage.getItem('rid.jwt'));

  // Collect failures per-run and emit a single aggregated warning to avoid
  // flooding the console (especially when the same template is re-rendered).
  const missingImages: string[] = [];
  const missingFonts: string[] = [];
  const erroredImages: string[] = [];
  const erroredFonts: string[] = [];

  const imagePromises = imageAssets.map(async (asset) => {
    try {
      const base64 = await fetchAssetContent(api, organizationId, asset.blobName, tokenGetter);
      if (base64) {
        images[asset.blobName] = base64;
      } else {
        missingImages.push(asset.blobName);
      }
    } catch {
      erroredImages.push(asset.blobName);
    }
  });

  // Resolve fonts via backend proxy to avoid CORS issues
  // The proxy returns the font binary which we convert to base64
  const fontPromises = fontAssets.map(async (asset) => {
    try {
      // Use proxy endpoint to fetch font as base64 (avoids CORS)
      const base64 = await fetchAssetContent(api, organizationId, asset.blobName, tokenGetter);

      if (base64) {
        // Inject @font-face with base64 data URL (works for both canvas and PDF)
        injectNewAssetFontFace(asset, base64);
        fonts[asset.blobName] = base64;
      } else {
        missingFonts.push(asset.blobName);
      }
    } catch {
      erroredFonts.push(asset.blobName);
    }
  });

  await Promise.all([...imagePromises, ...fontPromises]);

  if (imageAssets.length > 0) {
    console.log(`[legacyAssets] Resolved ${Object.keys(images).length}/${imageAssets.length} new images`);
  }
  if (fontAssets.length > 0) {
    console.log(`[legacyAssets] Resolved ${Object.keys(fonts).length}/${fontAssets.length} new fonts`);
  }
  if (missingImages.length > 0) {
    console.warn(`[legacyAssets] ${missingImages.length} image asset(s) could not be fetched (returned no content):`, missingImages);
  }
  if (missingFonts.length > 0) {
    console.warn(`[legacyAssets] ${missingFonts.length} font asset(s) could not be fetched (returned no content):`, missingFonts);
  }
  if (erroredImages.length > 0) {
    console.warn(`[legacyAssets] ${erroredImages.length} image asset(s) threw while fetching:`, erroredImages);
  }
  if (erroredFonts.length > 0) {
    console.warn(`[legacyAssets] ${erroredFonts.length} font asset(s) threw while fetching:`, erroredFonts);
  }

  return { images, fonts };
}

/**
 * Convert a Blob to a base64 data URL (exported for use in other modules)
 */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
